import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  User,
  Globe,
  FileCheck,
  Award,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { MINISTRY_DEFINITIONS } from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Personal Info", icon: User },
  { id: 2, title: "Location", icon: Globe },
  { id: 3, title: "Agreement", icon: FileCheck },
  { id: 4, title: "Complete", icon: Award },
];

export default function BecomeNational() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [stepErrors, setStepErrors] = useState({});
  const errorSummaryRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    state_province_region: "",
    social_links: "",
    reason_for_joining: "",
    skills_offerings: "",
    preferred_programs: [],
    agree_to_constitution: false,
    agree_to_covenant: false,
  });

  const scrollToErrors = () => {
    setTimeout(() => {
      errorSummaryRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const getStepErrors = (step) => {
    const newErrors = {};

    const fullName = formData.full_name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const country = formData.country.trim();
    const socialLinks = formData.social_links.trim();
    const reason = formData.reason_for_joining.trim();

    if (step === 1) {
      if (!fullName) {
        newErrors.full_name = "Full name is required.";
      } else if (fullName.length < 2) {
        newErrors.full_name = "Full name must be at least 2 characters.";
      }

      if (!email) {
        newErrors.email = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = "Enter a valid email address, like name@example.com.";
      }

      if (!phone) {
        newErrors.phone = "Phone number is required.";
      } else if (phone.replace(/[^\d]/g, "").length < 7) {
        newErrors.phone =
          "Enter a valid phone number with at least 7 digits.";
      }

      if (!socialLinks) {
        newErrors.social_links =
          "Social media links are required. Add at least one public profile link, website, or write N/A with a short explanation.";
      } else if (socialLinks.length < 5) {
        newErrors.social_links =
          "Please enter at least 5 characters for the social media section.";
      }
    }

    if (step === 2) {
      if (!country) {
        newErrors.country = "Country is required.";
      } else if (country.length < 2) {
        newErrors.country = "Country must be at least 2 characters.";
      }

      if (!reason) {
        newErrors.reason_for_joining =
          "Please explain why you want to become YaSHaRUN.";
      } else if (reason.length < 20) {
        newErrors.reason_for_joining =
          "Please give a little more detail. Write at least 20 characters.";
      }
    }

    if (step === 3) {
      if (!formData.agree_to_constitution) {
        newErrors.agree_to_constitution =
          "You must confirm that you read and agree to the Constitution.";
      }

      if (!formData.agree_to_covenant) {
        newErrors.agree_to_covenant =
          "You must confirm that you read and agree to the Internal Covenant.";
      }
    }

    return newErrors;
  };

  const validateStep = (step) => {
    const newErrors = getStepErrors(step);
    const hasErrors = Object.keys(newErrors).length > 0;

    setErrors(newErrors);
    setStepErrors((prev) => ({ ...prev, [step]: hasErrors }));

    if (hasErrors) {
      toast.error("Please fix the highlighted fields before continuing.");
      scrollToErrors();
      return false;
    }

    return true;
  };

  const validateAllStepsBeforeSubmit = () => {
    const step1Errors = getStepErrors(1);
    const step2Errors = getStepErrors(2);
    const step3Errors = getStepErrors(3);

    const allErrors = {
      ...step1Errors,
      ...step2Errors,
      ...step3Errors,
    };

    setErrors(allErrors);
    setStepErrors({
      1: Object.keys(step1Errors).length > 0,
      2: Object.keys(step2Errors).length > 0,
      3: Object.keys(step3Errors).length > 0,
    });

    if (Object.keys(allErrors).length > 0) {
      toast.error("Please fix the highlighted fields before submitting.");

      if (Object.keys(step1Errors).length > 0) {
        setCurrentStep(1);
      } else if (Object.keys(step2Errors).length > 0) {
        setCurrentStep(2);
      } else {
        setCurrentStep(3);
      }

      scrollToErrors();
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setErrors({});
      setStepErrors((prev) => ({ ...prev, [currentStep]: false }));
      setCurrentStep((prev) => Math.min(prev + 1, 4));

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 1));

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleSubmit = async () => {
    if (!validateAllStepsBeforeSubmit()) return;

    setLoading(true);

    const cleanedData = {
      ...formData,
      full_name: formData.full_name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      country: formData.country.trim(),
      city: formData.city.trim(),
      state_province_region: formData.state_province_region.trim(),
      social_links: formData.social_links.trim(),
      reason_for_joining: formData.reason_for_joining.trim(),
      skills_offerings: formData.skills_offerings.trim(),
      agree_to_constitution: Boolean(formData.agree_to_constitution),
      agree_to_covenant: Boolean(formData.agree_to_covenant),
    };

    try {
      await axios.post(`${API}/nationals`, cleanedData);
      setSubmitted(true);
      setErrors({});
      setStepErrors({});
      setCurrentStep(4);
      toast.success("Application submitted successfully!");

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } catch (error) {
      console.error("Failed to submit application:", error);

      const message =
        error.response?.data?.detail ||
        "The application could not be submitted. Please check the highlighted fields and try again.";

      toast.error(message);
      setErrors({ form: message });
      scrollToErrors();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    const normalizedValue =
      field === "agree_to_constitution" || field === "agree_to_covenant"
        ? value === true
        : value;

    setFormData((prev) => ({ ...prev, [field]: normalizedValue }));

    if (errors[field] || errors.form) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        delete updated.form;
        return updated;
      });
    }
  };

  const handleProgramToggle = (programKey) => {
    setFormData((prev) => ({
      ...prev,
      preferred_programs: prev.preferred_programs.includes(programKey)
        ? prev.preferred_programs.filter((p) => p !== programKey)
        : [...prev.preferred_programs, programKey],
    }));
  };

  const getFieldClass = (field) =>
    errors[field]
      ? "form-input border-red-500 bg-red-50 focus-visible:ring-red-500"
      : "form-input";

  const getStepStatus = (stepId) => {
    if (submitted && stepId === 4) return "complete";
    if (stepErrors[stepId]) return "error";
    if (currentStep > stepId) return "complete";
    if (currentStep === stepId) return "current";
    return "upcoming";
  };

  const goToStep = (stepId) => {
    if (stepId === currentStep || stepId === 4) return;

    if (stepId < currentStep) {
      setErrors({});
      setCurrentStep(stepId);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    for (let step = currentStep; step < stepId; step += 1) {
      if (!validateStep(step)) return;
    }

    setErrors({});
    setCurrentStep(stepId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen" data-testid="become-national-page">
      {/* Hero */}
      <section className="bg-royal-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
            Join the Nation
          </span>

          <h1 className="heading-hero mt-2 mb-4">Become YaSHaRUN</h1>

          <p className="text-slate-300 max-w-2xl">
            YaSHaRUN — The Beloved Upright of YaHUaH. SHAMA: hear and do. Continue in what you have been taught, moving from milk to meat and beyond, steadfast in the Turah of YaHUaH.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-parchment py-8 border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between">
            {steps.map((step, index) => {
              const status = getStepStatus(step.id);
              const isClickable =
                step.id < 4 && (step.id <= currentStep || currentStep > step.id);

              return (
                <div key={step.id} className="flex-1 relative">
                  <button
                    type="button"
                    onClick={() => goToStep(step.id)}
                    disabled={!isClickable}
                    className={`flex flex-col items-center w-full relative z-10 ${
                      isClickable ? "cursor-pointer" : "cursor-default"
                    }`}
                    aria-label={`Go to ${step.title}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
                        status === "error"
                          ? "bg-red-600 text-white"
                          : status === "complete"
                          ? "bg-kingdom-gold text-white"
                          : status === "current"
                          ? "bg-royal-blue text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {status === "error" ? (
                        <XCircle className="w-5 h-5" />
                      ) : status === "complete" ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>

                    <span
                      className={`text-xs mt-2 font-medium ${
                        status === "error"
                          ? "text-red-600"
                          : currentStep >= step.id
                          ? "text-royal-blue"
                          : "text-slate-500"
                      }`}
                    >
                      {step.title}
                    </span>
                  </button>

                  {index < steps.length - 1 && (
                    <div
                      className={`absolute top-5 left-1/2 w-full h-0.5 ${
                        currentStep > step.id
                          ? "bg-kingdom-gold"
                          : "bg-slate-200"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Content */}
      <section className="bg-white py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {Object.keys(errors).length > 0 && (
            <div
              ref={errorSummaryRef}
              className="mb-6 rounded border border-red-300 bg-red-50 p-4 text-red-800"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">
                    Please fix the highlighted fields.
                  </p>

                  <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
                    {Object.values(errors)
                      .filter(Boolean)
                      .map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="heading-section text-royal-blue">
                Personal Information
              </h2>

              <p className="text-slate-600">
                Tell us about yourself. Fields marked with * are required.
              </p>

              <div>
                <Label className="form-label">Full Name *</Label>
                <Input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  className={getFieldClass("full_name")}
                  placeholder="Your full name"
                  data-testid="national-name-input"
                  style={{ fontSize: "16px" }}
                  aria-invalid={Boolean(errors.full_name)}
                />
                {errors.full_name && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.full_name}
                  </p>
                )}
              </div>

              <div>
                <Label className="form-label">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className={getFieldClass("email")}
                  placeholder="your@email.com"
                  data-testid="national-email-input"
                  style={{ fontSize: "16px" }}
                  aria-invalid={Boolean(errors.email)}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <Label className="form-label">Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className={getFieldClass("phone")}
                  placeholder="+1 (555) 123-4567"
                  data-testid="national-phone-input"
                  style={{ fontSize: "16px" }}
                  aria-invalid={Boolean(errors.phone)}
                />
                {errors.phone && (
                  <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <Label className="form-label">
                  Social Media Links / Public Profile Links *
                </Label>
                <Textarea
                  value={formData.social_links}
                  onChange={(e) =>
                    handleChange("social_links", e.target.value)
                  }
                  className={`${getFieldClass("social_links")} min-h-[100px]`}
                  placeholder="Paste links to Facebook, TikTok, YouTube, Instagram, website, or another public profile. If you do not use social media, write N/A and briefly explain."
                  data-testid="national-social-input"
                  style={{ fontSize: "16px" }}
                  aria-invalid={Boolean(errors.social_links)}
                />
                {errors.social_links && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.social_links}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Required for review. Add at least one public profile link,
                  website, or write N/A with a short explanation if you do not
                  use social media.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Location & Reason */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="heading-section text-royal-blue">
                Location & Purpose
              </h2>

              <p className="text-slate-600">
                Where are you located and why do you want to join?
              </p>

              <div>
                <Label className="form-label">Country *</Label>
                <Input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                  className={getFieldClass("country")}
                  placeholder="Your country"
                  data-testid="national-country-input"
                  style={{ fontSize: "16px" }}
                  aria-invalid={Boolean(errors.country)}
                />
                {errors.country && (
                  <p className="text-red-600 text-sm mt-1">{errors.country}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">City</Label>
                  <Input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    className="form-input"
                    placeholder="City"
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="form-label">
                    State / Province / Region
                  </Label>
                  <Input
                    type="text"
                    value={formData.state_province_region}
                    onChange={(e) =>
                      handleChange("state_province_region", e.target.value)
                    }
                    className="form-input"
                    placeholder="State / Province / Region"
                    style={{ fontSize: "16px" }}
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">
                  Why Do You Want to Join? *
                </Label>
                <Textarea
                  value={formData.reason_for_joining}
                  onChange={(e) =>
                    handleChange("reason_for_joining", e.target.value)
                  }
                  className={`${getFieldClass(
                    "reason_for_joining"
                  )} min-h-[120px]`}
                  placeholder="Tell us about your journey, your beliefs, and why you want to become YaSHaRUN — The Beloved Upright of YaHUaH. Explain how you SHAMA (hear and do), continue what you have learned, and grow beyond repeatedly laying the foundation."
                  data-testid="national-reason-input"
                  style={{ fontSize: "16px" }}
                  aria-invalid={Boolean(errors.reason_for_joining)}
                />
                {errors.reason_for_joining && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.reason_for_joining}
                  </p>
                )}
              </div>

              <div>
                <Label className="form-label">Skills & Offerings</Label>
                <Textarea
                  value={formData.skills_offerings}
                  onChange={(e) =>
                    handleChange("skills_offerings", e.target.value)
                  }
                  className="form-input min-h-[100px]"
                  placeholder="What skills, trades, knowledge, service, tools, resources, or experience can you contribute to the nation?"
                  style={{ fontSize: "16px" }}
                />
              </div>

              <div>
                <Label className="form-label">
                  Preferred Ministries / Service Areas
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 max-h-80 overflow-y-auto p-3 border rounded bg-slate-50">
                  {MINISTRY_DEFINITIONS.map((ministry) => (
                    <label
                      key={ministry.key}
                      className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white p-2 rounded"
                    >
                      <Checkbox
                        checked={formData.preferred_programs.includes(
                          ministry.key
                        )}
                        onCheckedChange={() =>
                          handleProgramToggle(ministry.key)
                        }
                      />

                      <span>
                        <span className="font-semibold text-slate-700">
                          {ministry.label}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {ministry.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>

                <p className="text-xs text-slate-500 mt-2">
                  These selections help us understand where you may be able to
                  serve. Final ministry access is assigned after review.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Agreement */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="heading-section text-royal-blue">
                Constitution & Covenant Agreement
              </h2>

              <p className="text-slate-600">
                Before becoming YaSHaRUN, you must read and agree to both our
                Constitution and Internal Covenant.
              </p>

              <div className="bg-parchment p-6 rounded-sm border max-h-96 overflow-y-auto">
                <h3 className="font-heading text-lg font-bold text-royal-blue mb-4">
                  Summary of Key Points
                </h3>

                <ul className="space-y-3 text-sm text-slate-700">
                  <li>• The Turah of YaHUaH is the supreme law of the land</li>
                  <li>• Each YaSHaRUN is a free man or woman under YaHUaH</li>
                  <li>• We renounce Babylon, its idols, and its legal fictions</li>
                  <li>• We covenant together as qadashym called-out ones</li>
                  <li>• We pledge to settle disputes according to Turah</li>
                  <li>
                    • We proclaim freedom to the captives and right-ruling for
                    the oppressed
                  </li>
                </ul>

                <div className="mt-4 pt-4 border-t flex flex-col gap-2">
                  <Link
                    to="/governance/constitution"
                    target="_blank"
                    className="text-kingdom-gold hover:underline text-sm"
                  >
                    Read Full Constitution →
                  </Link>

                  <Link
                    to="/governance/declaration"
                    target="_blank"
                    className="text-kingdom-gold hover:underline text-sm"
                  >
                    Read Internal Covenant / Declaration →
                  </Link>
                </div>
              </div>

              <div
                className={`flex items-start gap-3 p-4 bg-white border-2 rounded-sm ${
                  errors.agree_to_constitution
                    ? "border-red-500 bg-red-50"
                    : "border-kingdom-gold"
                }`}
              >
                <Checkbox
                  id="agree-constitution"
                  checked={formData.agree_to_constitution}
                  onCheckedChange={(checked) =>
                    handleChange("agree_to_constitution", checked)
                  }
                  data-testid="national-agree-constitution-checkbox"
                />

                <label
                  htmlFor="agree-constitution"
                  className="text-sm cursor-pointer"
                >
                  <span className="font-semibold">
                    I have read and agree to the Constitution *
                  </span>
                  <span className="text-slate-600 block mt-1">
                    I understand the governance structure and covenant order of
                    The Kingdom of Yunah&apos;Shach&apos;Qal Yashar&apos;al.
                  </span>
                </label>
              </div>

              {errors.agree_to_constitution && (
                <p className="text-red-600 text-sm">
                  {errors.agree_to_constitution}
                </p>
              )}

              <div
                className={`flex items-start gap-3 p-4 bg-white border-2 rounded-sm ${
                  errors.agree_to_covenant
                    ? "border-red-500 bg-red-50"
                    : "border-royal-purple"
                }`}
              >
                <Checkbox
                  id="agree-covenant"
                  checked={formData.agree_to_covenant}
                  onCheckedChange={(checked) =>
                    handleChange("agree_to_covenant", checked)
                  }
                  data-testid="national-agree-covenant-checkbox"
                />

                <label
                  htmlFor="agree-covenant"
                  className="text-sm cursor-pointer"
                >
                  <span className="font-semibold">
                    I have read and agree to the Internal Covenant *
                  </span>
                  <span className="text-slate-600 block mt-1">
                    I am covenanting with YaHUaH and the people of
                    Yunah&apos;Shach&apos;Qal Yashar&apos;al to walk in
                    righteousness according to Turah.
                  </span>
                </label>
              </div>

              {errors.agree_to_covenant && (
                <p className="text-red-600 text-sm">
                  {errors.agree_to_covenant}
                </p>
              )}
            </div>
          )}

          {/* Step 4: Complete */}
          {currentStep === 4 && submitted && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>

              <h2 className="heading-section text-royal-blue mb-4">
                Application Submitted!
              </h2>

              <p className="text-slate-600 mb-8 max-w-md mx-auto">
                Thank you for applying to become YaSHaRUN — The Beloved Upright of YaHUaH — within The Kingdom of
                Yunah&apos;Shach&apos;Qal Yashar&apos;al. We will review your
                application and contact you soon.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button className="btn-gold">Return Home</Button>
                </Link>

                <Link to="/governance/constitution">
                  <Button variant="outline" className="btn-outline-royal">
                    Read Constitution
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 4 && (
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="btn-outline-royal"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="btn-gold"
                  data-testid="national-next-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  className="btn-gold"
                  disabled={loading}
                  data-testid="national-submit-btn"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
